// v1.2.0 — the serial of an M® Note, and the check symbol everything shares.
//
// A serial is 10 Crockford Base32 symbols: SERIES (1) · SEQUENCE (8) · CHECK (1).
//   series   — names the contract the note lives in ("A" = MNote v2.2 on Arc).
//              A redeploy is the next letter, so serials never collide across
//              deployments. The registry is SERIES below.
//   sequence — the contract's `seq` (its issuance counter), Base32, zero-padded
//              to 8 symbols: 2^40 ≈ 1.1 trillion notes per series.
//   check    — Luhn mod 32 over the 9 symbols before it (standard Luhn mod N:
//              the rightmost data symbol is the first one doubled). Catches
//              every single-symbol error and every adjacent swap, and stays
//              inside the typeable alphabet (Crockford's own mod-37 check can
//              land on * ~ $ = U).
// On the face: "M20 · A0000 040Z5". In the link: mdrop.it/A0000040Z5 — the
// identifier only, never the value. The contract knows none of this; it knows
// `seq`, and `noteBySeq(seq)` resolves the serial to a note id.
//
// The same check symbol closes a lock code (lockcode.ts): one function, two uses.

export const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // 32 symbols — no I L O U
export const SERIAL_LEN = 10;
export const SEQ_SYMBOLS = 8;
export const MAX_SEQ = 2n ** 40n - 1n;

/**
 * Series letter → the contract it names, the token that contract holds, and
 * the smallest amount it holds (`unit`, in the token's 6-decimal units). One
 * contract holds one token, so the series letter names the currency.
 * "A" = MNote v2.2 (2 Oct 2026, block 65140649): whole dollars.
 * "B" = MNoteCents v2.3 (4 Oct 2026, block 65479110): cents. v0.5.
 * "C" = MNoteCents v2.3 holding EURC (20 Oct 2026): euro, cents. app 1.4.55.
 * "D" = MNoteCents v2.3 holding GBPA (20 Oct 2026): pounds, pence. app 1.4.55.
 * `issue`: new notes in that currency go to this series (one per currency; USD's is the one
 * VITE_NOTES_ADDRESS names, the build's switch — ArcNoteSource).
 */
export interface Series { chainId: number; contract: string; token: string; currency: string; unit: bigint; issue?: true }
export const SERIES: Record<string, Series> = {
  A: {
    chainId: 5042002,
    contract: "0x72136c746d0fDD18311810d1C891C6C1A70a7486",
    token: "0x3600000000000000000000000000000000000000",
    currency: "USD",
    unit: 1_000_000n,
  },
  B: {
    chainId: 5042002,
    contract: "0x1d51e0D551a06F09b52428bBCA532e8BE4485dd4",
    token: "0x3600000000000000000000000000000000000000",
    currency: "USD",
    unit: 10_000n,
  },
  C: {
    chainId: 5042002,
    contract: "0xF702003BF7cb4B62934900d830bec3Df3e9D370C",
    token: "0x89B50855Aa3bE2F677cD6303Cec089B5F319D72a",
    currency: "EUR",
    unit: 10_000n,
    issue: true,
  },
  D: {
    chainId: 5042002,
    contract: "0x85992A4A933152f656824d7B2001F01A600E480D",
    token: "0xa42e82b5D25E84d107Cd8549CA432ef489CbaD32",
    currency: "GBP",
    unit: 10_000n,
    issue: true,
  },
};

/** Reading is forgiving: lower case, O→0, I/L→1, spaces and hyphens ignored. */
export function normalizeSymbols(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");
}

const val = (c: string): number => {
  const i = CROCKFORD.indexOf(c);
  if (i < 0) throw new Error(`not a Crockford symbol: ${c}`);
  return i;
};

/**
 * Luhn mod N (N = 32) check symbol for a string of Crockford symbols.
 * Standard algorithm: from the right, double every other value (starting with
 * the rightmost), reduce doubled values by summing their base-32 digits, and
 * pick the symbol that brings the total to 0 mod 32.
 */
export function luhn32(symbols: string): string {
  let sum = 0;
  let factor = 2; // the rightmost data symbol is doubled (the check will sit to its right)
  for (let i = symbols.length - 1; i >= 0; i--) {
    const add = val(symbols[i]) * factor;
    sum += Math.floor(add / 32) + (add % 32);
    factor = factor === 2 ? 1 : 2;
  }
  const check = (32 - (sum % 32)) % 32;
  return CROCKFORD[check];
}

/** True when the last symbol is the Luhn mod 32 check of the ones before it. */
export function luhn32Valid(symbolsWithCheck: string): boolean {
  if (symbolsWithCheck.length < 2) return false;
  try {
    return luhn32(symbolsWithCheck.slice(0, -1)) === symbolsWithCheck.slice(-1);
  } catch {
    return false;
  }
}

/** The 8-symbol Base32 form of a sequence number. */
export function encodeSeq(seq: bigint | number): string {
  let n = BigInt(seq);
  if (n < 1n || n > MAX_SEQ) throw new Error(`seq out of range: ${n}`);
  let out = "";
  while (n > 0n) {
    out = CROCKFORD[Number(n % 32n)] + out;
    n /= 32n;
  }
  return out.padStart(SEQ_SYMBOLS, "0");
}

export function decodeSeq(symbols: string): bigint {
  let n = 0n;
  for (const c of symbols) n = n * 32n + BigInt(val(c));
  return n;
}

export interface Serial {
  series: string;
  seq: bigint;
  /** The 10 symbols, e.g. "A0000040Z5". */
  text: string;
}

/** Build a serial from the series letter and the contract's seq. */
export function makeSerial(series: string, seq: bigint | number): Serial {
  const s = normalizeSymbols(series);
  if (s.length !== 1) throw new Error(`series must be one symbol: ${series}`);
  const body = s + encodeSeq(seq);
  return { series: s, seq: BigInt(seq), text: body + luhn32(body) };
}

/** Parse what a person typed, scanned or pasted. null when it isn't a serial. */
export function parseSerial(input: string): Serial | null {
  const t = normalizeSymbols(input);
  if (t.length !== SERIAL_LEN || !luhn32Valid(t)) return null;
  const seq = decodeSeq(t.slice(1, 1 + SEQ_SYMBOLS));
  if (seq < 1n) return null;
  return { series: t[0], seq, text: t };
}

/** "A0000 040Z5" — two fives, as printed on the face. */
export function formatSerial(serial: Serial | string): string {
  const t = typeof serial === "string" ? serial : serial.text;
  return `${t.slice(0, 5)} ${t.slice(5)}`;
}

/** "20" for whole dollars, "14.50" with cents (v0.5). `value` in dollars. */
export function noteAmountText(value: number | bigint): string {
  if (typeof value === "bigint") return value.toString();
  const cents = Math.round(value * 100);
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

/** "M20 · A0000 040Z5", "M14.50 · B0000 040Z3" — the face line. `value` in dollars. */
export function faceLine(value: number | bigint, serial: Serial | string): string {
  return `M${noteAmountText(value)} · ${formatSerial(serial)}`;
}

/** The path part of the note's link: "/A0000040Z5". */
export function linkPath(serial: Serial | string): string {
  return "/" + (typeof serial === "string" ? serial : serial.text);
}

/** The path segments of a link, as typed or printed: a path, a full URL, or "mdrop.it/…"; no query, no fragment. */
function pathSegments(pathOrUrl: string): string[] {
  let path = pathOrUrl.trim().replace(/[?#].*$/, "");
  const url = path.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]*(.*)$/i);
  if (url) path = url[1];
  else if (!path.startsWith("/")) {
    const slash = path.indexOf("/");
    if (slash > 0 && path.slice(0, slash).includes(".")) path = path.slice(slash); // "mdrop.it/…"
  }
  return path.split("/").filter(Boolean);
}

/** A cash link's secret as a path segment (app 1.4.46): its 16 symbols exactly. Anything else after a
 * serial ("/A0000040Z5/USD25") is not a note's link (v0.3's rule: the serial is the whole path). */
const SECRET_SEGMENT = /^[0-9A-Za-z]{16}$/;

/**
 * The serial a link stands for, or null. Takes a path ("/A0000040Z5"), a full
 * URL, or the printed form without a scheme ("mdrop.it/A0000040Z5"); the query
 * and the fragment are ignored.
 *
 * v0.3 — the serial must be the WHOLE path: exactly one segment, ten symbols,
 * valid check. Before, any ten-symbol segment matched, so a pay link whose
 * reference happened to pass the check (/CODE/USD25/RENT2026AT — one reference
 * of that length in 32) or a Drop id (/d/…) read as a note.
 *
 * app 1.4.46 (Alexander, 19 Oct) — or the serial and a cash link's secret as a second segment:
 * "/A0000040Z5/7KQ2VN8XP4DRWE1Z". Snapchat hands a link on without its `#` part, which is where the
 * secret was; it keeps the path. A pay link's first segment is a wallet code (eight symbols), never a
 * serial (ten, with its check), so the two can't be confused. Links with `#` keep working.
 */
export function serialFromPath(pathOrUrl: string): Serial | null {
  const segs = pathSegments(pathOrUrl);
  if (segs.length < 1 || segs.length > 2 || !/^[0-9A-Za-z]{10}$/.test(segs[0])) return null;
  if (segs.length === 2 && !SECRET_SEGMENT.test(segs[1])) return null;
  return parseSerial(segs[0]);
}

/**
 * app 1.4.46 — what a note's link carries as its second path segment ("/A0000040Z5/<secret>"), as
 * written (16 symbols, not yet normalised or checked against the note), or null.
 */
export function linkSecretFromPath(pathOrUrl: string): string | null {
  const segs = pathSegments(pathOrUrl);
  return segs.length === 2 && serialFromPath(pathOrUrl) ? segs[1] : null;
}
